import { useEffect, useState } from 'react';
import OrgChooser from "./components/org-selector/org-selector.component";
import OtpPage from './components/otp/otp.component';
import { GetAllApps } from './components/services/auth.service';

import { ClearQueryStringCookies, CreateOtpQueryStringCookie, FetchTenantIdFromCookie, FetchTenantNameFromCookie } from "./components/services/cookie.service";

/**
 * @typedef {object} KdsJwtPayload
 * @property {string} gasurl
 * @property {string} SubscriptionKey
 * @property {string} TenantId
 * @property {string} IsSaas
 * @property {object} [Registration]
 */

/**
 * @param {string} token
 * @returns {KdsJwtPayload}
 */
export const parseJwt = (token) => {
    const payloadSegment = token.split('.')[1];
    if (!payloadSegment) {
        throw new Error('Invalid JWT.');
    }
    const base64 = payloadSegment.replace(/-/g, '+').replace(/_/g, '/');
    const paddedBase64 = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    const jsonPayload = decodeURIComponent(
        atob(paddedBase64)
            .split('')
            .map((character) => `%${(`00${character.charCodeAt(0).toString(16)}`).slice(-2)}`)
            .join('')
    );
    const payload = JSON.parse(jsonPayload);
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
        throw new Error('Invalid JWT payload.');
    }
    return payload;
};

async function continueToApp(appName, clientCode, backendOtp) {
    const apps = await GetAllApps();
    const app = apps.find((item) => item.name === appName);
    if (!app) {
        throw new Error('Selected application is unavailable.');
    }
    if(appName === 'kds' || appName === 'dmb' || appName === 'board')
    {
        appName = appName === 'kds' ? 'kds' : 'board';
    }
    CreateOtpQueryStringCookie(clientCode, backendOtp, appName);
    let response;
    let appToken;
    try {
            if (appName === 'kds' || appName === 'dmb' || appName === 'board') {
                const appNameSpace = appName === 'kds' ? 'kds' : 'board'; 
                const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '';
                const appInfoResponse = await fetch(`${apiBaseUrl}/${appNameSpace}/GetAppInfo/`, {
                method: 'GET',
                credentials: 'include'
            });
            if (!appInfoResponse || appInfoResponse.status !== 200) {
                throw new Error(`Invalid registration. Please try again.`);
            }
            const appInfo = await appInfoResponse.json();
            if (!appInfo || typeof appInfo !== 'object' || Array.isArray(appInfo) || Object.keys(appInfo).length === 0) {
                throw new Error('Invalid registration. Please try again.');
            }
            if (appInfo?.Properties?.KDSToken || appInfo?.Properties?.dmbtoken) {
                appToken = appInfo.Properties?.KDSToken || appInfo?.Properties?.dmbtoken;
                if (!appToken) {
                    throw new Error('Invalid registration. Please try again.');
                }
                const tokenPayload = parseJwt(appToken);
                if (!tokenPayload.Registration) {
                    throw new Error('Invalid registration. Please try again.');
                }
                const registration = JSON.parse(tokenPayload.Registration);
                if (registration?.Message && registration?.Message !== 'Station registered successfully') {
                    throw new Error('Invalid registration. Please try again.');
                }
            }
        }
        const appRequestOptions = { credentials: 'include' };
        if (appToken) {
            appRequestOptions.headers = { Authorization: `Bearer ${appToken}` };
        }
        response = await fetch(app.url, appRequestOptions);
    } catch (error) {
        if (error instanceof Error && error.message.startsWith('Unable to retrieve KDS app info')) {
            throw error;
        }
        throw new Error(`Invalid registration. Please try again.`);
    }
    if (response.status !== 200) {
        throw new Error('Invalid registration. Please try again.');
    }
    const html = await response.text();
    window.history.replaceState(null, '', app.url);
    window.document.open();
    window.document.write(html);
    window.document.close();
}

function App() {
    const [route, setRoute] = useState(window.location.hash.slice(1));
    useEffect(() => {
        const handleHashChange = () => setRoute(window.location.hash.slice(1));
        window.addEventListener('hashchange', handleHashChange);
        return () => window.removeEventListener('hashchange', handleHashChange);
    }, []);

    const [pathname, query] = route.split('?');
    const appName = new URLSearchParams(query).get('app');
    const isSupportedOtpApp = appName === 'kds' || appName === 'dmb';
    const returnHome = () => {
        ClearQueryStringCookies();
        window.history.replaceState(null, '', window.location.pathname || '/');
        setRoute('');
    };

    useEffect(() => {
        if (pathname !== '/otp' || isSupportedOtpApp) {
            return;
        }
        ClearQueryStringCookies();
        window.history.replaceState(null, '', window.location.pathname || '/');
        setRoute('');
    }, [pathname, isSupportedOtpApp]);

    if (pathname === '/otp') {
        if (!isSupportedOtpApp) {
            return null;
        }
        return <OtpPage
            appName={appName}
            onContinue={continueToApp}
            onVerified={continueToApp}
            onBack={returnHome}
        />;
    }

    return <OrgChooser OrgTenantId={FetchTenantIdFromCookie()} OrgName={FetchTenantNameFromCookie()} />
}

export default App